// src/components/SicurezzaCantiere.js
// ═══════════════════════════════════════════════════════════════════════════
// SICUREZZA DI CANTIERE (ottobre 2026, piano Pro)
// ═══════════════════════════════════════════════════════════════════════════
// • Scheda "🦺 Sicurezza" della commessa: attivazione, responsabile, domande sul cantiere,
//   checklist adattiva, storico.
// • AvvisoSicurezza: avviso rosso NON bloccante, mostrato in cima alla commessa, nel
//   giornale di cantiere e (in forma breve) nella programmazione. Si chiude solo con un
//   motivo scritto; chi e quando restano nello storico.
// Regole: utils/sicurezzaCantiere.js — Scritture: utils/sicurezzaCantiereDb.js
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { useCanSee } from "../hooks/useCanSee";
import { messaggioErrore } from "../utils/erroriUtente";
import { DOMANDE, GRUPPI, AVVISO_LIMITI, SOGLIA_NOTIFICA_UOMINI_GIORNO, vociChecklist, statoVoce, riepilogoSicurezza, avvisoAttivo, testoAvviso, contesto } from "../utils/sicurezzaCantiere";
import { attivaSicurezzaCantiere, salvaConfig, impostaVoce, cambiaResponsabile, chiudiAvviso } from "../utils/sicurezzaCantiereDb";
import { RigaSede } from "./SediCliente";
import { useIdoneitaDitte } from "./IdoneitaDitte";
import { testoProblemiDitta } from "../utils/documentiSubappaltatori";

const ROSSO = "#b91c1c";
const inp = { width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12.5, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };
const btn = (pieno, colore = "#7c3aed") => ({ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", border: pieno ? "none" : `1px solid ${colore}`, background: pieno ? colore : "#fff", color: pieno ? "#fff" : colore, fontFamily: "'Inter',sans-serif" });
const dataOra = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "";

// ── Dati condivisi ─────────────────────────────────────────────────────────
export function useSicurezzaCantiere(projectId, projectData) {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId: orgId } = useOrg();
  const canSee = useCanSee();
  const [scheda, setScheda] = useState(undefined); // undefined = caricamento, null = non attiva
  const [org, setOrg] = useState({});
  useEffect(() => {
    if (!projectId) return;
    return onSnapshot(doc(db, "sicurezzaCantiere", projectId), s => setScheda(s.exists() ? s.data() : null), () => setScheda(null));
  }, [projectId]);
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(doc(db, "organizations", orgId), s => setOrg(s.exists() ? s.data() : {}), () => {});
  }, [orgId]);
  const voci = useMemo(() => scheda ? vociChecklist(projectData, scheda.config, org.sicurezzaVociAzienda) : [], [scheda, projectData, org.sicurezzaVociAzienda]);
  const avviso = useMemo(() => avvisoAttivo(scheda, voci), [scheda, voci]);
  const gestore = isOrgAdmin || isPlatformOwner || canSee("sicurezza-cantiere");
  const puoGestire = gestore || (scheda && scheda.responsabileUid && scheda.responsabileUid === currentUser?.uid);
  return { scheda, voci, avviso, org, orgId, gestore, puoGestire, currentUser };
}

// null finché l'elenco non è arrivato (così non si attiva con un responsabile senza nome)
function useUtenti(orgId) {
  const [utenti, setUtenti] = useState(null);
  useEffect(() => {
    if (!orgId) return;
    return onSnapshot(query(collection(db, "users"), where("organizationId", "==", orgId)),
      s => setUtenti(s.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.enabled !== false)
        .sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "", "it"))), () => setUtenti([]));
  }, [orgId]);
  return utenti;
}

// ── Finestra per chiudere l'avviso (motivo obbligatorio) ──────────────────
function ChiudiAvvisoModal({ projectId, avviso, onClose }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [motivo, setMotivo] = useState("");
  const [salvo, setSalvo] = useState(false);
  const conferma = async () => {
    if (motivo.trim().length < 5) { addToast("Scrivi il motivo (almeno qualche parola)", "error"); return; }
    setSalvo(true);
    try { await chiudiAvviso(projectId, avviso, motivo.trim(), currentUser); addToast("Avviso chiuso: resta registrato nello storico", "success"); onClose(); }
    catch (err) { addToast(messaggioErrore(err, "chiudere l'avviso"), "error"); setSalvo(false); }
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Chiudi l'avviso di sicurezza</h3>
        <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
          L'avviso non blocca il lavoro. Chiudendolo, il tuo nome, la data e il motivo restano registrati nello storico della sicurezza di questa commessa e non si possono cancellare.
          Gli adempimenti restano aperti nella scheda 🦺 Sicurezza; l'avviso torna se ne compaiono di nuovi.
        </p>
        <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} placeholder="Motivo (es. documenti già verificati in sede, in attesa di caricamento)" style={{ ...inp, resize: "vertical", marginBottom: 12 }} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btn(false, "#6b7280")}>Annulla</button>
          <button onClick={conferma} disabled={salvo} style={btn(true, ROSSO)}>{salvo ? "..." : "Chiudi avviso"}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Avviso rosso non bloccante. compatto: una riga sola (giornale, programmazione).
 * onApri: porta alla scheda Sicurezza (se c'è dove portare).
 */
export function AvvisoSicurezza({ projectId, projectData, onApri, compatto = false }) {
  const S = useSicurezzaCantiere(projectId, projectData);
  const [chiudi, setChiudi] = useState(false);
  if (!S.scheda || !S.avviso.attivo) return null;
  return (
    <>
      <div style={{ background: "#fef2f2", borderBottom: compatto ? "none" : "1px solid #fecaca", border: compatto ? "1px solid #fecaca" : undefined, borderRadius: compatto ? 8 : 0,
        padding: compatto ? "6px 10px" : "8px 16px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0, margin: compatto ? "0 0 10px" : 0 }}>
        <span style={{ fontSize: 16 }}>🦺</span>
        {/* Pulsanti subito dopo il titolo, a sinistra (ott 2026): a destra finivano sotto
            l'avatar dell'utente e lontani dal testo */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: ROSSO }}>{testoAvviso(S.avviso)}</span>
            {onApri && <button onClick={onApri} style={{ ...btn(true, ROSSO), padding: "4px 10px" }}>Apri</button>}
            <button onClick={() => setChiudi(true)} title="Segna l'avviso come chiuso (motivo obbligatorio, resta nello storico)" style={{ ...btn(false, ROSSO), padding: "4px 9px" }}>Chiudi avviso</button>
          </div>
          {!compatto && <div style={{ fontSize: 11, color: "#7f1d1d", marginTop: 2 }}>Addetto alla sicurezza: {S.scheda.responsabileNome || "da indicare"} · l'avviso non blocca il lavoro</div>}
        </div>
      </div>
      {chiudi && <ChiudiAvvisoModal projectId={projectId} avviso={S.avviso} onClose={() => setChiudi(false)} />}
    </>
  );
}

// ── Attivazione su una commessa ────────────────────────────────────────────
function AttivaSicurezza({ projectId, projectData, S }) {
  const { addToast } = useToast();
  const elencoUtenti = useUtenti(S.orgId);
  const utenti = elencoUtenti || [];
  const [resp, setResp] = useState("");
  useEffect(() => { if (!resp && S.org.responsabileSicurezzaUid) setResp(S.org.responsabileSicurezzaUid); }, [S.org.responsabileSicurezzaUid, resp]);
  const [salvo, setSalvo] = useState(false);
  const attiva = async () => {
    if (!resp) { addToast("Scegli l'addetto alla sicurezza e alla documentazione di cantiere", "error"); return; }
    setSalvo(true);
    try {
      await attivaSicurezzaCantiere({ projectId, project: projectData, organizationId: S.orgId,
        responsabile: { uid: resp, nome: utenti.find(u => u.uid === resp)?.displayName || "" }, utente: S.currentUser });
      addToast("Sicurezza di cantiere attivata: l'addetto alla sicurezza riceve notifica ed email", "success");
    } catch (err) { addToast(messaggioErrore(err, "attivare la sicurezza di cantiere"), "error"); setSalvo(false); }
  };
  return (
    <div style={{ maxWidth: 560, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: "#1e1b4b", marginBottom: 6 }}>Questa commessa non è segnata come cantiere</div>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Se si tratta di un cantiere temporaneo o mobile (Titolo IV del D.Lgs. 81/08), attiva la sicurezza di cantiere: l'addetto alla sicurezza riceve subito
        notifica ed email, risponde a poche domande e Nexiora prepara la checklist degli adempimenti adatta a questo cantiere.
      </p>
      {/* Ogni cantiere deve avere la sicurezza attiva: può attivarla chiunque lavori sulla commessa.
          Cambiare poi il responsabile e gestire tutte le checklist resta ai gestori. */}
      {(
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={resp} onChange={e => setResp(e.target.value)} style={{ ...inp, width: 260 }}>
            <option value="">— Addetto alla sicurezza e alla documentazione di cantiere —</option>
            {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>)}
          </select>
          <button onClick={attiva} disabled={salvo || elencoUtenti === null} style={btn(true)}>{salvo ? "..." : elencoUtenti === null ? "Carico i colleghi…" : "Attiva sicurezza di cantiere"}</button>
        </div>
      )}
    </div>
  );
}

// ── Scheda della commessa ──────────────────────────────────────────────────
export default function SicurezzaCantiereTab({ projectId, projectData }) {
  const S = useSicurezzaCantiere(projectId, projectData);
  const { addToast } = useToast();
  const elencoUtenti = useUtenti(S.orgId);
  const utenti = elencoUtenti || [];
  const [cfg, setCfg] = useState(null);       // risposte in modifica
  const [nota, setNota] = useState(null);     // { voce, stato, testo }
  const [storico, setStorico] = useState(false);
  const [chiudi, setChiudi] = useState(false);
  // imprese esterne della commessa con documenti scaduti o mancanti (piano Gold, W413)
  const idoneita = useIdoneitaDitte(S.orgId);
  const nonInRegola = useMemo(() => !idoneita ? [] : (projectData?.impreseEsterne || [])
    .filter(i => i.subappaltatoreId && idoneita.get(i.subappaltatoreId)?.semaforo.colore === "rosso")
    .map(i => ({ id: i.subappaltatoreId, nome: i.nome, testo: testoProblemiDitta(idoneita.get(i.subappaltatoreId).problemi) })), [idoneita, projectData?.impreseEsterne]);

  if (S.scheda === undefined) return <div style={{ padding: 30, color: "#9ca3af", fontSize: 12.5 }}>Caricamento…</div>;
  if (S.scheda === null) return <div style={{ padding: 20 }}><AttivaSicurezza projectId={projectId} projectData={projectData} S={S} /></div>;

  const sc = S.scheda;
  const x = contesto(projectData, sc.config);
  const r = riepilogoSicurezza(S.voci, sc.stati || {});
  const inModifica = cfg !== null || !sc.configConfermata;
  const risposte = cfg || { ...x.cfg };

  const salvaRisposte = async () => {
    const descr = DOMANDE.filter(d => risposte[d.id]).map(d => d.testo.replace(/\?$/, "")).join("; ") || "nessun rischio particolare indicato";
    try { await salvaConfig(projectId, risposte, S.currentUser, descr); setCfg(null); addToast("Risposte salvate: checklist aggiornata", "success"); }
    catch (err) { addToast(messaggioErrore(err, "salvare le risposte"), "error"); }
  };
  const segna = async (voce, stato, testo = "") => {
    try { await impostaVoce(projectId, voce, stato, testo, S.currentUser); setNota(null); }
    catch (err) { addToast(messaggioErrore(err, "aggiornare l'adempimento"), "error"); }
  };
  const nuovoResp = async (uid) => {
    try { await cambiaResponsabile(projectId, { uid, nome: utenti.find(u => u.uid === uid)?.displayName || "" }, S.currentUser); addToast("Addetto alla sicurezza aggiornato: riceve notifica ed email", "success"); }
    catch (err) { addToast(messaggioErrore(err, "cambiare l'addetto alla sicurezza"), "error"); }
  };

  const gruppi = Object.keys(GRUPPI).map(g => ({ id: g, label: GRUPPI[g], voci: S.voci.filter(v => v.gruppo === g) })).filter(g => g.voci.length > 0);
  const percent = r.totale ? Math.round((r.fatte + r.nonApplicabili) / r.totale * 100) : 0;

  return (
    <div style={{ padding: 20, maxWidth: 980 }}>
      {nonInRegola.length > 0 && projectData?.statoCommessa !== "chiusa" && (
        <div role="alert" style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #dc2626", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 12.5, color: "#991b1b" }}>
          <b>🔴 Imprese esterne non in regola in questo cantiere</b>
          {nonInRegola.map(x => <div key={x.id} style={{ marginTop: 3 }}><b>{x.nome}</b>: {x.testo}.</div>)}
          <div style={{ fontSize: 11.5, marginTop: 4, color: "#b91c1c" }}>Prima di farle lavorare, fatti mandare i documenti e verificali in Documenti dei subappaltatori.</div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: "#1e1b4b", margin: 0 }}>🦺 Sicurezza di cantiere</h3>
          {projectData?.sede?.indirizzo && <div style={{ marginTop: 2 }}><RigaSede sede={projectData.sede} piccola /></div>}
          <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>
            {x.nEsecutrici} impres{x.nEsecutrici === 1 ? "a esecutrice" : "e esecutrici"}
            {x.ug.totale > 0 && <> · circa {Math.round(x.ug.totale)} uomini-giorno{!x.ug.completo && " (stima incompleta)"}</>}
            {x.ug.interni === null && x.cfg.noiEseguiamo && <> · uomini-giorno non calcolabili: indica nella commessa data di inizio, data di fine e squadra</>}
            {x.notificaDovuta && <> · notifica preliminare dovuta</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#6b7280" }}>Addetto alla sicurezza e alla documentazione</span>
          {S.gestore
            ? <select value={sc.responsabileUid || ""} onChange={e => nuovoResp(e.target.value)} style={{ ...inp, width: 220 }}>
                <option value="">— da indicare —</option>
                {utenti.map(u => <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>)}
              </select>
            : <b style={{ fontSize: 12.5 }}>{sc.responsabileNome || "da indicare"}</b>}
        </div>
      </div>

      {S.avviso.attivo && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: ROSSO, flex: 1 }}>{testoAvviso(S.avviso)}</span>
          <button onClick={() => setChiudi(true)} title="Segna l'avviso come chiuso (motivo obbligatorio, resta nello storico)" style={btn(false, ROSSO)}>Chiudi avviso</button>
        </div>
      )}
      {!S.avviso.attivo && S.avviso.chiuso && sc.avviso && (
        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
          Avviso chiuso da <b>{sc.avviso.chiusoDaNome || "—"}</b> il {dataOra(sc.avviso.chiusoIl)} — motivo: {sc.avviso.motivo}
        </div>
      )}

      {/* Domande sul cantiere */}
      <div style={{ background: "#fff", border: `1px solid ${sc.configConfermata ? "#e5e7eb" : "#fca5a5"}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b" }}>{sc.configConfermata ? "Il cantiere" : "Rispondi a queste domande per preparare la checklist"}</div>
          {sc.configConfermata && cfg === null && S.puoGestire && <button onClick={() => setCfg({ ...x.cfg })} style={btn(false)}>Modifica risposte</button>}
        </div>
        {inModifica && S.puoGestire ? (
          <>
            {["imprese", "rischi"].map(g => (
              <div key={g} style={{ marginBottom: 8 }}>
                {g === "rischi" && <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", margin: "6px 0 4px" }}>Rischi particolari presenti</div>}
                {DOMANDE.filter(d => d.gruppo === g).map(d => (
                  <label key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: "#374151", padding: "3px 0", cursor: "pointer" }}>
                    <input type="checkbox" checked={!!risposte[d.id]} onChange={e => setCfg({ ...risposte, [d.id]: e.target.checked })} style={{ accentColor: "#7c3aed" }} />
                    {d.testo}
                  </label>
                ))}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={salvaRisposte} style={btn(true)}>Salva risposte</button>
              {sc.configConfermata && <button onClick={() => setCfg(null)} style={btn(false, "#6b7280")}>Annulla</button>}
            </div>
          </>
        ) : !sc.configConfermata ? (
          <div style={{ fontSize: 12, color: ROSSO }}>L'addetto alla sicurezza ({sc.responsabileNome || "da indicare"}) deve ancora rispondere alle domande sul cantiere.</div>
        ) : (
          <div style={{ fontSize: 12, color: "#4b5563" }}>
            {DOMANDE.filter(d => x.cfg[d.id]).map(d => d.testo.replace(/\?$/, "")).join(" · ") || "Nessun rischio particolare indicato."}
          </div>
        )}
      </div>

      {/* Checklist */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b" }}>Adempimenti</div>
        <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 4, maxWidth: 260 }}>
          <div style={{ width: `${percent}%`, height: "100%", borderRadius: 4, background: r.aperte ? "#f59e0b" : "#059669" }} />
        </div>
        <span style={{ fontSize: 11.5, color: "#6b7280" }}>{r.fatte + r.nonApplicabili}/{r.totale} · {r.aperte} da fare</span>
      </div>
      {gruppi.map(g => (
        <div key={g.id} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 4 }}>{g.label}</div>
          {g.voci.map(v => {
            const st = statoVoce(sc.stati, v.id);
            const info = sc.stati?.[v.id];
            return (
              <div key={v.id} style={{ background: "#fff", border: `1px solid ${st === "daFare" ? "#fecaca" : "#e5e7eb"}`, borderRadius: 9, padding: "8px 12px", marginBottom: 5 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 14, marginTop: 1 }}>{st === "fatto" ? "✅" : st === "nonApplicabile" ? "➖" : "🔴"}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: st === "nonApplicabile" ? "#9ca3af" : "#111827" }}>{v.titolo}</div>
                    <div style={{ fontSize: 10.5, color: "#9ca3af" }}>{v.rif}{v.dettaglio ? ` — ${v.dettaglio}` : ""}</div>
                    {info && <div style={{ fontSize: 10.5, color: "#4b5563", marginTop: 2 }}>{st === "fatto" ? "Fatto" : "Non applicabile"} · {info.chi} · {dataOra(info.il)}{info.nota ? ` — ${info.nota}` : ""}</div>}
                    {nota?.voce.id === v.id && (
                      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                        <input autoFocus value={nota.testo} onChange={e => setNota({ ...nota, testo: e.target.value })}
                          placeholder={nota.stato === "nonApplicabile" ? "Perché non si applica (obbligatorio)" : "Nota o riferimento al documento (facoltativa)"} style={{ ...inp, flex: 1 }} />
                        <button onClick={() => { if (nota.stato === "nonApplicabile" && nota.testo.trim().length < 3) { addToast("Scrivi perché non si applica", "error"); return; } segna(v, nota.stato, nota.testo.trim()); }} style={btn(true)}>Conferma</button>
                        <button onClick={() => setNota(null)} style={btn(false, "#6b7280")}>Annulla</button>
                      </div>
                    )}
                  </div>
                  {S.puoGestire && nota?.voce.id !== v.id && (
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      {st === "daFare" ? <>
                        <button onClick={() => setNota({ voce: v, stato: "fatto", testo: "" })} title="Segna l'adempimento come fatto (puoi aggiungere una nota)" style={btn(false, "#059669")}>Fatto</button>
                        <button onClick={() => setNota({ voce: v, stato: "nonApplicabile", testo: "" })} title="Segna come non applicabile a questo cantiere (motivo obbligatorio)" style={btn(false, "#6b7280")}>N/A</button>
                      </> : <button onClick={() => segna(v, "daFare")} title="Segna di nuovo come da fare (resta nello storico)" style={btn(false, "#6b7280")}>Riapri</button>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {x.ug.totale >= SOGLIA_NOTIFICA_UOMINI_GIORNO * 0.8 && x.ug.totale < SOGLIA_NOTIFICA_UOMINI_GIORNO && !x.piuImprese && (
        <div style={{ fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 10px", marginBottom: 10 }}>
          Il cantiere è vicino ai {SOGLIA_NOTIFICA_UOMINI_GIORNO} uomini-giorno: oltre questa soglia la notifica preliminare diventa obbligatoria.
        </div>
      )}
      <div style={{ fontSize: 10.5, color: "#9ca3af", margin: "10px 0" }}>{AVVISO_LIMITI}</div>

      <button onClick={() => setStorico(s => !s)} style={{ background: "none", border: "none", padding: 0, fontSize: 11.5, color: "#7c3aed", cursor: "pointer", fontWeight: 600 }}>
        {storico ? "▾" : "▸"} Storico ({(sc.storico || []).length})
      </button>
      {storico && (
        <div style={{ marginTop: 6, background: "#f9fafb", borderRadius: 8, padding: "8px 12px" }}>
          {(sc.storico || []).slice().reverse().map((h, i) => (
            <div key={i} style={{ fontSize: 11, color: "#4b5563", padding: "2px 0" }}>
              <span style={{ color: "#9ca3af" }}>{dataOra(h.il)} · {h.chi || "—"}</span> — <b>{h.azione}</b>{h.dettaglio ? `: ${h.dettaglio}` : ""}
            </div>
          ))}
        </div>
      )}
      {chiudi && <ChiudiAvvisoModal projectId={projectId} avviso={S.avviso} onClose={() => setChiudi(false)} />}
    </div>
  );
}

/**
 * Programmazione settimanale: riga rossa con i cantieri programmati nella settimana che
 * hanno adempimenti di sicurezza aperti (non bloccante; l'avviso si chiude dalla commessa).
 * commesse: elenco delle commesse (con i dati per la checklist); idInProgramma: id presenti nella griglia.
 */
export function AvvisiSicurezzaProgrammazione({ orgId, commesse = [], idInProgramma = [], onApri }) {
  const [schede, setSchede] = useState([]);
  const [org, setOrg] = useState({});
  useEffect(() => {
    if (!orgId) return;
    const u1 = onSnapshot(query(collection(db, "sicurezzaCantiere"), where("organizationId", "==", orgId)),
      s => setSchede(s.docs.map(d => d.data())), () => setSchede([]));
    const u2 = onSnapshot(doc(db, "organizations", orgId), s => setOrg(s.exists() ? s.data() : {}), () => {});
    return () => { u1(); u2(); };
  }, [orgId]);
  const chiavi = new Set(idInProgramma.filter(Boolean));
  const elenco = schede.filter(sc => chiavi.has(sc.projectId)).map(sc => {
    const p = commesse.find(c => c.id === sc.projectId);
    if (!p || p.statoCommessa === "chiusa") return null;
    const a = avvisoAttivo(sc, vociChecklist(p, sc.config, org.sicurezzaVociAzienda));
    return a.attivo ? { id: p.id, nome: `${p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}${p.name}`, a } : null;
  }).filter(Boolean);
  if (elenco.length === 0) return null;
  return (
    <div style={{ background: "#fef2f2", borderBottom: "1px solid #fecaca", padding: "6px 16px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flexShrink: 0 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: ROSSO }}>🦺 Cantieri in programma con sicurezza da completare:</span>
      {elenco.map(e => (
        <button key={e.id} onClick={() => onApri && onApri(e.id)} title={testoAvviso(e.a)}
          style={{ fontSize: 11.5, fontWeight: 600, padding: "2px 9px", borderRadius: 10, border: "1px solid #fca5a5", background: "#fff", color: ROSSO, cursor: "pointer" }}>
          {e.nome} · {e.a.daConfigurare ? "da impostare" : `${e.a.aperte} da fare`}
        </button>
      ))}
    </div>
  );
}
